import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import { roleDemo, quitterDemo } from '../demo/demo';

const AuthContext = createContext(null);
const OFFLINE_KEY = 'evolink_offline_user';

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(p => error ? p.reject(error) : p.resolve());
  failedQueue = [];
};

// URLs qui ne doivent JAMAIS déclencher un refresh automatique
const NO_REFRESH_URLS = ['/auth/refresh', '/auth/login', '/auth/me', '/auth/register'];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef(null);
  const API = API_URL;

  const refreshToken = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
      return true;
    } catch {
      return false;
    }
  }, [API]);

  const scheduleRefresh = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = setTimeout(async () => {
      const ok = await refreshToken();
      if (ok) scheduleRefresh();
      else setUser(null);
    }, 7.5 * 60 * 60 * 1000);
  }, [refreshToken]);

  // Intercepteur axios — retry automatique sur 401
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      res => res,
      async err => {
        const original = err.config;
        const url = original?.url || '';
        const isNoRefresh = NO_REFRESH_URLS.some(u => url.includes(u));

        if (err.response?.status === 401 && !original._retry && !isNoRefresh) {
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            }).then(() => axios(original)).catch(e => Promise.reject(e));
          }
          original._retry = true;
          isRefreshing = true;
          try {
            await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
            processQueue(null);
            return axios(original);
          } catch (refreshErr) {
            processQueue(refreshErr);
            setUser(null);
            return Promise.reject(refreshErr);
          } finally {
            isRefreshing = false;
          }
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [API]);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
      scheduleRefresh();
      try { localStorage.setItem(OFFLINE_KEY, JSON.stringify({ id: data.id, name: data.name, role: data.role, email: data.email, admin_validated: data.admin_validated })); } catch (e) { /* ignore */ }
    } catch (err) {
      // Hors ligne (aucune réponse du serveur) : on garde l'écran du dernier utilisateur connecté
      // pour les outils qui marchent sans réseau (autocontrôle PV). Les données restent protégées côté serveur.
      let offlineUser = null;
      if (!err?.response && typeof navigator !== 'undefined' && navigator.onLine === false) {
        try { offlineUser = JSON.parse(localStorage.getItem(OFFLINE_KEY) || 'null'); } catch (e) { offlineUser = null; }
      }
      setUser(offlineUser ? { ...offlineUser, offline: true } : null);
    } finally {
      setLoading(false);
    }
  }, [API, scheduleRefresh]);

  useEffect(() => {
    checkAuth();
    return () => { if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current); };
  }, [checkAuth]);

  const login = async (email, password, geoData = {}) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password, ...geoData }, { withCredentials: true });
      if (data && data.require_code) return { success: false, requireCode: true, pending: data.pending, emailMasque: data.email_masque };
      setUser(data);
      scheduleRefresh();
      try { localStorage.setItem(OFFLINE_KEY, JSON.stringify({ id: data.id, name: data.name, role: data.role, email: data.email, admin_validated: data.admin_validated })); } catch (e) { /* ignore */ }
      return { success: true, user: data };
    } catch (err) {
      const message = err?.response?.data?.detail || "Identifiants incorrects";
      return { success: false, error: message };
    }
  };

  // Deuxième étape de connexion admin : code reçu par email
  const verifyCode = async (pending, code) => {
    try {
      const { data } = await axios.post(`${API}/auth/verify-code`, { pending, code }, { withCredentials: true });
      setUser(data);
      scheduleRefresh();
      try { localStorage.setItem(OFFLINE_KEY, JSON.stringify({ id: data.id, name: data.name, role: data.role, email: data.email, admin_validated: data.admin_validated })); } catch (e) { /* ignore */ }
      return { success: true, user: data };
    } catch (err) {
      return { success: false, error: err?.response?.data?.detail || 'Code incorrect' };
    }
  };

  const register = async (emailOrObj, password, name, role = 'tech') => {
    let payload;
    if (typeof emailOrObj === 'object' && emailOrObj !== null) {
      payload = { role: 'tech', ...emailOrObj };
    } else {
      payload = { email: emailOrObj, password, name, role };
    }
    try {
      const { data } = await axios.post(`${API}/auth/register`, payload, { withCredentials: true });
      // Ne pas setUser ici - le compte nécessite validation admin
      return { success: true, message: data?.message || 'Compte créé' };
    } catch (err) {
      const message = err?.response?.data?.detail || "Erreur lors de l'inscription";
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    if (roleDemo()) { quitterDemo(); return; }
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch {}
    try { localStorage.removeItem(OFFLINE_KEY); } catch (e) { /* ignore */ }
    try { if (navigator.serviceWorker && navigator.serviceWorker.controller) navigator.serviceWorker.controller.postMessage({ type: 'clear-api' }); if (window.caches) window.caches.delete('evolink-api-v1'); } catch (e) { /* ignore */ }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, verifyCode, register, logout, checkAuth, refreshToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
